/**
 * The text each derived cell of the Connected AI clients grid shows.
 *
 * Pure functions, exported, and used by BOTH the column's `valueGetter` and its
 * `renderCell`. That pairing is the point: the DataGrid's own column menu — the
 * funnel and sort arrows users reach for without thinking — filters on the
 * underlying value, never on what the chip renders. A column whose cell derives
 * its text and whose `field` points at a raw property therefore gives two
 * different answers to the same question, and the rows that disagree simply
 * vanish from the filtered set with no error.
 *
 * Separate module rather than inline so they can be tested directly: the grid
 * renders no column headers under jsdom, so asserting on the mapping is the only
 * way to cover it.
 */

export interface ConnectorCellRow {
  status?: string;
  lastUsedAt?: string;
  revokedAt?: string;
  revokedReason?: string;
}

/**
 * Why this connector is off, when it is off.
 *
 * The reason matters to an operator in a way the raw enum does not: a connector
 * the SYSTEM switched off (the person was disabled or removed) comes back on its
 * own if the account is re-enabled, and one an operator revoked does not. Someone
 * looking at this table is usually deciding whether to expect it back.
 */
export function connectorStatusLabel(row: ConnectorCellRow): string {
  if (row.status !== 'revoked') {
    if (row.status === 'active') return 'Connected';
    // 'expired' is a DEFINED variant of the status union, so passing the raw
    // enum through would read as a bug beside 'Connected' and 'Disconnected'.
    // Anything genuinely unrecognised still falls through untranslated — a
    // status this build has never heard of should look unfamiliar, not be
    // quietly relabelled as something reassuring.
    if (row.status === 'expired') return 'Expired';
    return row.status ?? 'unknown';
  }
  switch (row.revokedReason) {
    case 'user-disabled':
      return 'Disconnected (account disabled)';
    case 'removed-from-org':
      return 'Disconnected (removed from org)';
    case 'admin-revoked':
      return 'Disconnected by an admin';
    default:
      return 'Disconnected';
  }
}

/**
 * When it was last used — or, once it is off, when it was cut off.
 *
 * A revoked connector's `lastUsedAt` is frozen at whatever it was, so showing it
 * under a "Last used" heading reads as though the credential is still in use.
 */
export function connectorLastUsedLabel(row: ConnectorCellRow): string {
  if (row.status === 'revoked') {
    return row.revokedAt ? `Cut off ${formatDate(row.revokedAt)}` : 'Cut off';
  }
  return row.lastUsedAt ? formatDate(row.lastUsedAt) : 'Never used';
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

/**
 * The timestamp the Last-used column should SORT by.
 *
 * Separate from the label because they answer different questions. The label
 * has to say whether a date is a last use or a cut-off; the sort just needs
 * the moment. Returning the formatted string for both made the grid order the
 * column alphabetically — 'Cut off 01/01/2025' before '06/05/2026' — which is
 * the kind of thing an operator notices and quietly stops trusting.
 *
 * `null` rather than a sentinel date: MUI puts nulls at the end of either sort
 * direction, which is where "never used" belongs.
 */
export function connectorLastUsedSortValue(row: ConnectorCellRow): Date | null {
  const iso = row.status === 'revoked' ? row.revokedAt : row.lastUsedAt;
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
